import { useEffect, useState } from "react";
import api from "../../services/api";
import {
  Users, BookOpen, ShoppingBag, TrendingUp,
  ArrowUpRight, Activity, Clock, Library, WalletCards
} from "lucide-react";
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";

function StatCard({ label, value, icon: Icon, color, trend }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800 flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1">{value}</p>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-green-500 text-xs font-semibold">
            <ArrowUpRight size={14} /> {trend}
          </div>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800 animate-pulse">
      <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
      <div className="h-8 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
    </div>
  );
}

export default function InstituteDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/institute/stats")
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: "Total Users", value: stats.total_users, icon: Users, color: "bg-blue-500", trend: "+12% this month" },
    { label: "Total Courses", value: stats.total_courses, icon: BookOpen, color: "bg-violet-500", trend: "+3 new" },
    { label: "Total Revenue", value: `₹${stats.total_revenue?.toLocaleString()}`, icon: TrendingUp, color: "bg-emerald-500", trend: "+18% this month" },
    { label: "Total Orders", value: stats.total_orders, icon: ShoppingBag, color: "bg-amber-500", trend: "+8% this week" },
    { label: "Digital Books", value: stats.total_books, icon: Library, color: "bg-rose-500", trend: "Library Growing" },
    { label: "Active Subscriptions", value: stats.active_subscriptions || 0, icon: WalletCards, color: "bg-indigo-500", trend: "Student plans" },
  ] : [];

  const planLabels = {
    free_courses: "Free Courses",
    paid_courses: "Paid Courses",
    monthly_subscription: "Monthly",
    lifetime_access: "Lifetime",
    freemium: "Freemium",
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Overview</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Welcome back, Institute. Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {loading
          ? [1, 2, 3, 4].map(i => <SkeletonCard key={i} />)
          : cards.map(c => <StatCard key={c.label} {...c} />)
        }
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="xl:col-span-2 bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white">Revenue Overview</h2>
              <p className="text-xs text-slate-500 mt-0.5">Monthly revenue trend</p>
            </div>
            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/20">
              <Activity size={18} className="text-blue-600" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats?.revenue_chart || []}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 40px rgba(0,0,0,0.12)" }}
                formatter={v => [`₹${v}`, "Revenue"]}
              />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fill="url(#revenueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Feed */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
          <h2 className="font-bold text-slate-900 dark:text-white mb-6">Recent Activity</h2>
          <div className="space-y-6">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-1/4" />
                  </div>
                </div>
              ))
            ) : stats?.recent_activity?.length > 0 ? (
              stats.recent_activity.map((act, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 text-blue-600">
                    <Clock size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{act.msg}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{act.time}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 text-sm py-8">No recent activity</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-bold text-slate-900 dark:text-white">Subscription Plans</h2>
            <p className="text-xs text-slate-500 mt-0.5">Active student access by model</p>
          </div>
          <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/20">
            <WalletCards size={18} className="text-indigo-600" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {["free_courses", "paid_courses", "monthly_subscription", "lifetime_access", "freemium"].map((key) => {
            const item = stats?.subscription_breakdown?.find(plan => plan.plan_key === key);
            return (
              <div key={key} className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">{planLabels[key]}</p>
                <p className="mt-2 text-2xl font-black text-slate-900 dark:text-white">{item?.count || 0}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
